'use client';
// The offline documents' dashboard chrome (BL-2343, ADR 066 — Offline Mode § 2): the same
// SessionProvider and DashboardShell as online, with the session built on the device instead of
// by the async (dashboard) layout, which cannot run without a server.
import { Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CloudOff } from 'lucide-react';
import type { SessionBootstrapPayload } from '@auctogon/core-admin/schemas/responses/session';

import type { AuthOps } from '../../api-client/auth-ops';
import DashboardShell from '../DashboardShell';
import { SessionProvider } from '../session/SessionProvider';
import SessionSplash from '../session/SessionSplash';
import EmptyState from '../ui/EmptyState';
import { loadOfflineSession } from './offline-session';
import { useHandBackWhenOnline } from './useHandBackWhenOnline';

/** Props for {@link OfflineSessionShell}. */
export interface OfflineSessionShellProps {
  /** The app shell's Better Auth operations (BL-1600), as `AppSessionProvider` passes them. */
  authOps: AuthOps;
  children: ReactNode;
}

/**
 * What an offline document shows when the device holds no usable session. It never redirects to
 * sign-in: offline that navigation fails and the service worker serves this document again.
 *
 * @param props.status - why there is no session.
 * @returns the state.
 */
function NoOfflineSession({ status }: { status: 'unauthenticated' | 'error' }) {
  return (
    <main className="s-v2 min-h-screen p-density-page" data-testid="offline-session-unavailable" data-status={status}>
      <EmptyState
        icon={CloudOff}
        title={status === 'unauthenticated' ? 'Sign in to work offline' : 'Could not open the data on this device'}
        variant="v2"
      />
    </main>
  );
}

/**
 * Mount the dashboard chrome over the device's stored session. Renders the splash until the
 * session is read, so the static prerender and the first client render agree.
 *
 * @param props - the auth operations and the offline screen.
 * @returns the chrome and the screen, or the no-session state.
 * @sideEffect Reads the device store through the offline transport; hands back to the online
 *   screen once the server answers again.
 */
export function OfflineSessionShell({ authOps, children }: OfflineSessionShellProps) {
  const [session, setSession] = useState<SessionBootstrapPayload | null>(null);
  useHandBackWhenOnline();

  useEffect(() => {
    let live = true;
    void loadOfflineSession().then((payload) => {
      if (live) setSession(payload);
    });
    return () => {
      live = false;
    };
  }, []);

  // SessionProvider pins the first promise it is given, so hand it one per resolved session.
  const sessionPromise = useMemo(() => (session ? Promise.resolve(session) : null), [session]);

  if (!session || !sessionPromise) return <SessionSplash />;
  if (session.status !== 'authenticated') return <NoOfflineSession status={session.status} />;
  return (
    <Suspense fallback={<SessionSplash />}>
      <SessionProvider sessionPromise={sessionPromise} authOps={authOps}>
        <DashboardShell initialCollapsed={false}>{children}</DashboardShell>
      </SessionProvider>
    </Suspense>
  );
}
